import { useState, useEffect, useCallback } from 'react'
import { TicketListViewProps, TicketListView } from './list-view/list-view'

import { pollingRequest, RootState, useAppDispatch } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { DataFetchingState } from '@AppState/types'
import { LoadingAnimation } from '@Components/loading/loading'
import { fetchTicketResults } from '@AppState/ticket-info'
import { TicketInfo } from '@Config/ticket-form-types'
import { PollingActions } from '@AppState/polling/polling-slice'
import { mapListResults } from './ticket-form-summary/utils'

const CLASS_NAME = 'gby-k2g-ticket-list-mode'
const POLLING_ID = 'fetchTicketResults'

export const TicketListMode = () => {
  const ticketModeData = useSelector((state: RootState) => state.ticketMode)
  const hostConfig = useSelector((state: RootState) => state.hostConfig.config)
  const pollingState = useSelector((state: RootState) => state.polling)
  const dispatch = useAppDispatch()

  const [ticketListProps, setTicketListProps] = useState<TicketListViewProps | null>(null)

  const isTicketComplete = useCallback(() => {
    return ticketModeData.activeTicketResults && ticketModeData.activeTicketResults.ticketComplete
  }, [ticketModeData])

  useEffect(() => {
    if (isTicketComplete()) {
      dispatch(PollingActions.clearPollingInterval(POLLING_ID))
    } else if (
      ticketModeData.activeTicket &&
      !isTicketComplete() &&
      !pollingState?.intervalMap[POLLING_ID]
    ) {
      if (ticketModeData.fetchState !== DataFetchingState.PENDING) {
        dispatch(
          pollingRequest<TicketInfo>(fetchTicketResults(ticketModeData.activeTicket), POLLING_ID),
        )
      }
    }
  }, [dispatch, isTicketComplete, pollingState, ticketModeData])

  useEffect(() => {
    document.documentElement.style.setProperty('--app-background-color', `#E5E5E5`)
    document.documentElement.style.setProperty('--app-background-glow-color', `#E5E5E5`)

    return () => {
      dispatch(PollingActions.clearPollingInterval(POLLING_ID))
    }
  }, [dispatch])

  useEffect(() => {
    const currTicket = ticketModeData.activeTicket
    const ticketResults = ticketModeData.activeTicketResults

    if (!currTicket || !hostConfig || !ticketResults) return

    const listResults = mapListResults(
      ticketResults.scoredDrawResults,
      currTicket.numberSelections,
      currTicket.extraDrawAddOnSelection?.name,
      hostConfig,
    )

    const sortedResults = listResults.sort((a, b) => b.drawNumber - a.drawNumber)

    const listViewProps: TicketListViewProps = {
      ticketInfo: currTicket,
      drawResults: sortedResults,
      matchColors: hostConfig.addOns.map((addOn) => {
        return { name: addOn.name, color: addOn.listResultColor }
      }),
    }

    setTicketListProps(listViewProps)
  }, [ticketModeData, hostConfig])

  if (!ticketModeData) {
    return <LoadingAnimation />
  }

  return (
    <div className={CLASS_NAME}>
      {ticketListProps ? <TicketListView {...ticketListProps} /> : null}
    </div>
  )
}
